import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import type { PanelImperativeHandle } from 'react-resizable-panels';

import {
  COMPANY_SWITCHER_WIDTH,
  getSidebarDefaultPx,
  getSidebarMinPx,
} from 'src/constants/uiConsts';
import { useFeatureFlags } from 'src/context';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import { useDrawerWidth } from 'src/features/drawer/useDrawerWidth';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store/reduxTypes';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';

export interface ShellContextValue {
  drawerWidth: number;
  sidebarOpen: boolean;
  openMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  isMobile: boolean;
  showContextBar: boolean;
  setShowContextBar: (show: boolean) => void;
  contextBarContentMinWidth: number | undefined;
  setContextBarContentMinWidth: (minWidth: number | undefined) => void;
  sidebarPanelRef: React.RefObject<PanelImperativeHandle | null>;
  onSidebarPanelResize: (sizeInPixels: number) => void;
  onCompanySwitcherPanelResize: (sizeInPixels: number) => void;
  isResizableSidebarActive: boolean;
  setDisableResizableSidebar: (disabled: boolean) => void;
  isDesktopSidebarCollapsed: boolean;
  /** Records the user's sidebar choice from the group's layout changes. */
  onSidebarLayoutChanged: (
    layout: Record<string, number>,
    meta: { isUserInteraction: boolean },
  ) => void;
  /** Reapplies that choice once the resizable sidebar has remounted and measured. */
  restoreSidebarIntent: () => void;
  toggleDesktopSidebar: () => void;
  onSidebarCollapse: () => void;
  onSidebarExpand: () => void;
  /** When true, AppShell portaled page content across shell layout branches. */
  portalPageContent: boolean;
  setPortalPageContent: (enabled: boolean) => void;
}

export const ShellContext = React.createContext<ShellContextValue | null>(null);

export const ShellProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  // down('sm') maps to md (960px) via the v4 breakpoint shim — intentionally
  // swaps the nav sidebar to the legacy/mobile drawer below that width.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { EnableNewSidebar, EnableStudioSidebar } = useFeatureFlags();
  const companySwitcherVisibility = useCompanySwitcherVisibility();
  const [isResizableSidebarDisabled, setDisableResizableSidebar] =
    React.useState(false);
  const isResizableSidebarActive =
    EnableNewSidebar && !isMobile && !isResizableSidebarDisabled;

  const sidebarPanelRef = React.useRef<PanelImperativeHandle>(null);
  // Initialise with the min panel width so drawerWidth is non-zero on the first
  // render, before the ResizablePanel fires its onResize callback.
  const [sidebarPanelSizePx, setSidebarPanelSizePx] = React.useState(() =>
    getSidebarMinPx(EnableStudioSidebar),
  );

  const onSidebarPanelResize = React.useCallback(
    (sizeInPixels: number) => setSidebarPanelSizePx(sizeInPixels),
    [],
  );

  // The company column is a resizable panel of its own beside the nav, so the
  // drawer width the rest of the shell lays out against is the sum of the two.
  const [companySwitcherPanelSizePx, setCompanySwitcherPanelSizePx] =
    React.useState(COMPANY_SWITCHER_WIDTH);

  const onCompanySwitcherPanelResize = React.useCallback(
    (sizeInPixels: number) => setCompanySwitcherPanelSizePx(sizeInPixels),
    [],
  );

  const companySwitcherOffset =
    companySwitcherVisibility.visibility === 'desktop'
      ? companySwitcherPanelSizePx
      : 0;

  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] =
    React.useState(false);

  const onSidebarCollapse = React.useCallback(() => {
    setIsDesktopSidebarCollapsed(true);
  }, []);

  const onSidebarExpand = React.useCallback(() => {
    setIsDesktopSidebarCollapsed(false);
  }, []);

  // What the user last chose for the desktop sidebar: hidden, or a width in
  // pixels. The mobile drawer unmounts the resizable sidebar, and the layout
  // the group persists is a percentage that a window resize can collapse or
  // re-scale, so returning to desktop reapplies this instead. Seeded from the
  // first measured layout, then changed only by drags and the toggle.
  const sidebarIntentRef = React.useRef<{
    collapsed: boolean;
    px: number;
  } | null>(null);

  const rememberSidebarIntent = React.useCallback(() => {
    const panel = sidebarPanelRef.current;
    if (!panel) return;
    const collapsed = panel.isCollapsed();
    sidebarIntentRef.current = {
      collapsed,
      px: collapsed
        ? (sidebarIntentRef.current?.px ??
          getSidebarDefaultPx(EnableStudioSidebar))
        : panel.getSize().inPixels,
    };
  }, [EnableStudioSidebar]);

  const onSidebarLayoutChanged = React.useCallback(
    (_layout: Record<string, number>, meta: { isUserInteraction: boolean }) => {
      if (meta.isUserInteraction || !sidebarIntentRef.current) {
        rememberSidebarIntent();
      }
    },
    [rememberSidebarIntent],
  );

  const restoreSidebarIntent = React.useCallback(() => {
    const panel = sidebarPanelRef.current;
    const intent = sidebarIntentRef.current;
    if (!panel || !intent) return;
    if (intent.collapsed) {
      if (!panel.isCollapsed()) panel.collapse();
      return;
    }
    if (panel.isCollapsed()) panel.expand();
    panel.resize(intent.px);
  }, []);

  const toggleDesktopSidebar = React.useCallback(() => {
    const panel = sidebarPanelRef.current;
    if (!panel) return;
    if (panel.isCollapsed()) {
      panel.expand();
    } else {
      panel.collapse();
    }
    // The toggle is the user's choice too, but the group reports imperative
    // changes as non-interactions.
    rememberSidebarIntent();
  }, [sidebarPanelRef, rememberSidebarIntent]);

  const drawerWidth = useDrawerWidth(
    isResizableSidebarActive
      ? sidebarPanelSizePx + companySwitcherOffset
      : null,
  );

  const sidebarOpen = useAppSelector(
    (state: RootState) => state.ui.primarySideBarMobile.isOpen,
  );

  const [showContextBar, setShowContextBar] = React.useState(false);
  const [contextBarContentMinWidth, setContextBarContentMinWidth] =
    React.useState<number | undefined>(undefined);
  const [portalPageContent, setPortalPageContent] = React.useState(false);

  // Dispatch the desired state directly to avoid stale-closure issues
  // that occur with a toggle pattern (where the callback captures sidebarOpen).
  const openMobileSidebar = React.useCallback(() => {
    dispatch(togglePrimarySidebarMobile({ isOpen: true }));
  }, [dispatch]);

  const closeMobileSidebar = React.useCallback(() => {
    dispatch(togglePrimarySidebarMobile({ isOpen: false }));
  }, [dispatch]);

  const value = React.useMemo<ShellContextValue>(
    () => ({
      drawerWidth,
      sidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      isMobile,
      showContextBar,
      setShowContextBar,
      contextBarContentMinWidth,
      setContextBarContentMinWidth,
      sidebarPanelRef,
      onSidebarPanelResize,
      onCompanySwitcherPanelResize,
      isResizableSidebarActive,
      setDisableResizableSidebar,
      isDesktopSidebarCollapsed,
      toggleDesktopSidebar,
      onSidebarCollapse,
      onSidebarExpand,
      onSidebarLayoutChanged,
      restoreSidebarIntent,
      portalPageContent,
      setPortalPageContent,
    }),
    [
      drawerWidth,
      sidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      isMobile,
      showContextBar,
      contextBarContentMinWidth,
      sidebarPanelRef,
      onSidebarPanelResize,
      onCompanySwitcherPanelResize,
      isResizableSidebarActive,
      setDisableResizableSidebar,
      isDesktopSidebarCollapsed,
      toggleDesktopSidebar,
      onSidebarCollapse,
      onSidebarExpand,
      onSidebarLayoutChanged,
      restoreSidebarIntent,
      portalPageContent,
      setPortalPageContent,
    ],
  );

  return (
    <ShellContext.Provider value={value}>{children}</ShellContext.Provider>
  );
};

export function useShell(): ShellContextValue {
  const context = React.useContext(ShellContext);
  if (context === null) {
    throw new Error('useShell must be used within a ShellProvider');
  }
  return context;
}
